import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Building2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabaseApplicantService } from '../services/supabase/applicants';
import { useNavigationRefresh } from '../hooks/useNavigationRefresh';
import { useApplicantFilters } from '../hooks/useApplicantFilters';
import ApplicantFilters from '../components/Applicants/ApplicantFilters';
import ActiveApplicantFilters from '../components/Applicants/ActiveApplicantFilters';
import ApplicantTable from '../components/Applicants/ApplicantTable';
import ApplicantDetailsModal from '../components/Applicants/ApplicantDetailsModal';
import type { Applicant, ApplicantFilter } from '../types/applicant';

export default function Applicants() {
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedApplicant, setSelectedApplicant] = useState<Applicant | null>(null);
  const [filters, setFilters] = useState<ApplicantFilter>({
    name: null,
    contactName: null,
    email: null
  });
  
  // Use the custom hook to handle navigation refreshes
  useNavigationRefresh(['applicants']);

  // Use React Query for data fetching
  const { data: applicants = [], isLoading, isError } = useQuery({
    queryKey: ['applicants'],
    queryFn: supabaseApplicantService.getAll,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Apply filters
  const filteredApplicants = useApplicantFilters(applicants, searchQuery, filters);

  // Extract unique values for filter dropdowns
  const uniqueNames = React.useMemo(() => 
    Array.from(new Set(applicants.map(applicant => applicant.appl_name))).sort(),
    [applicants]
  );

  const uniqueContactNames = React.useMemo(() => 
    Array.from(new Set(applicants.map(applicant => applicant.appl_cont_name).filter(Boolean) as string[])).sort(),
    [applicants]
  );

  const uniqueEmails = React.useMemo(() => 
    Array.from(new Set(applicants.map(applicant => applicant.appl_email).filter(Boolean) as string[])).sort(),
    [applicants]
  );

  const handleRemoveFilter = (key: keyof ApplicantFilter) => {
    setFilters(prev => ({ ...prev, [key]: null }));
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <ArrowLeft className="h-6 w-6" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Applicants</h1>
              <p className="text-sm text-gray-500">
                View and manage lease applicants
              </p>
            </div>
          </div>
          <div className="w-full sm:w-auto">
            <ApplicantFilters
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              isFilterOpen={isFilterOpen}
              onFilterToggle={() => setIsFilterOpen(!isFilterOpen)}
              filters={filters}
              onFilterChange={(newFilters) => setFilters(prev => ({ ...prev, ...newFilters }))}
              names={uniqueNames}
              contactNames={uniqueContactNames}
              emails={uniqueEmails}
            />
          </div>
        </div>
        <ActiveApplicantFilters filters={filters} onRemoveFilter={handleRemoveFilter} />
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {isError ? (
          <div className="p-4 text-red-500">
            There was an error fetching applicants. Please try again.
          </div>
        ) : filteredApplicants.length === 0 ? (
          <div className="bg-white p-8 text-center">
            <Building2 className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No applicants</h3>
            <p className="mt-1 text-sm text-gray-500">
              No applicants found matching your search criteria.
            </p>
          </div>
        ) : (
          <ApplicantTable
            applicants={filteredApplicants}
            onViewDetails={setSelectedApplicant}
          />
        )}
      </div>

      {selectedApplicant && (
        <ApplicantDetailsModal
          applicant={selectedApplicant}
          onClose={() => setSelectedApplicant(null)}
        />
      )}
    </div>
  );
}
